import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { TransportCompanyFilters } from '../../types';
import { useI18n } from '../../i18n';

interface TransportCompanyFilterSidebarProps {
  filters: TransportCompanyFilters;
  onFiltersChange: (filters: Partial<TransportCompanyFilters>) => void;
  onClose?: () => void;
}

export function TransportCompanyFilterSidebar({ filters, onFiltersChange, onClose }: TransportCompanyFilterSidebarProps) {
  const { t } = useI18n();

  return (
    <Box sx={{ p: 2.5 }}>
      {onClose && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">{t('filters.title')}</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      )}

      <TextField
        label={t('common.taxId')}
        size="small"
        fullWidth
        placeholder={t('filters.nipPlaceholder')}
        value={filters.taxIdentificationNumber ?? ''}
        onChange={(event) => onFiltersChange({ taxIdentificationNumber: event.target.value || undefined })}
        sx={{ mb: 2.5 }}
      />

      <TextField
        label={t('filters.city')}
        size="small"
        fullWidth
        placeholder={t('filters.cityPlaceholder')}
        value={filters.city ?? ''}
        onChange={(event) => onFiltersChange({ city: event.target.value || undefined })}
        sx={{ mb: 2.5 }}
      />

      <FormControl sx={{ mb: 2.5 }}>
        <FormLabel sx={{ mb: 0.5, fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}>{t('filters.companyType')}</FormLabel>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={filters.verified ?? false}
              onChange={(event) => onFiltersChange({ verified: event.target.checked || undefined })}
            />
          }
          label={<Typography variant="body2">{t('filters.onlyVerified')}</Typography>}
        />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={filters.highlighted ?? false}
              onChange={(event) => onFiltersChange({ highlighted: event.target.checked || undefined })}
            />
          }
          label={<Typography variant="body2">{t('filters.onlyHighlighted')}</Typography>}
        />
      </FormControl>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <FormLabel sx={{ mb: 0.5, fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}>{t('filters.sort')}</FormLabel>
        <Select
          size="small"
          value={`${filters.sortBy ?? 'createdAt'}_${filters.sortOrder ?? 'desc'}`}
          onChange={(event) => {
            const [sortBy, sortOrder] = event.target.value.split('_');
            onFiltersChange({ sortBy, sortOrder: sortOrder as 'asc' | 'desc' });
          }}
        >
          <MenuItem value="createdAt_desc">{t('listings.newest')}</MenuItem>
          <MenuItem value="createdAt_asc">{t('listings.oldest')}</MenuItem>
        </Select>
      </FormControl>

      <Button
        variant="outlined"
        fullWidth
        size="small"
        onClick={() => onFiltersChange({ taxIdentificationNumber: undefined, city: undefined, verified: undefined, highlighted: undefined, sortBy: 'createdAt', sortOrder: 'desc' })}
      >{t('filters.clear')}</Button>
    </Box>
  );
}

